import React, { useEffect, useRef, useState } from 'react';
import { TaskItem } from '../../types/tasks';
import { useTasks } from '../../context/TasksContext';
import { MATERIAL_STATUS, isMaterial, money } from '../../utils/projectValue';

/**
 * The ⋯ menu's Material panel: something to buy for the project — how many,
 * the price of one, where from — and whether it's needed, ordered or in.
 * Saving it on a task makes it a material. Clicking away keeps what was typed.
 */
const MaterialPanel: React.FC<{ item: TaskItem; readOnly: boolean; onClose: () => void }> = ({ item, readOnly, onClose }) => {
  const { updateItem } = useTasks();
  const [qty, setQty] = useState(item.qty != null ? String(item.qty) : '');
  const [price, setPrice] = useState(item.cost != null ? String(item.cost) : '');
  const [vendor, setVendor] = useState(item.vendor || '');
  const [url, setUrl] = useState(item.buy_url || '');
  const [status, setStatus] = useState(item.progress >= 100 ? 100 : item.progress > 0 ? 50 : 0);

  const numOrNull = (s: string) => (s.trim() === '' || !Number.isFinite(Number(s)) ? null : Math.max(0, Number(s)));
  const patch = () => {
    const p: Partial<TaskItem> = {};
    if (!isMaterial(item)) p.node_kind = 'material';
    if (numOrNull(qty) !== (item.qty ?? null)) p.qty = numOrNull(qty);
    if (numOrNull(price) !== (item.cost ?? null)) p.cost = numOrNull(price);
    if ((vendor.trim() || null) !== (item.vendor || null)) p.vendor = vendor.trim() || null;
    const link = url.trim() && !/^https?:\/\//i.test(url.trim()) ? `https://${url.trim()}` : url.trim();
    if ((link || null) !== (item.buy_url || null)) p.buy_url = link || null;
    const was = item.progress >= 100 ? 100 : item.progress > 0 ? 50 : 0;
    if (status !== was) p.progress = status;
    return p;
  };
  const save = () => {
    const p = patch();
    // Opened on a plain task and left untouched: don't turn it into a material.
    if (!isMaterial(item) && Object.keys(p).length === 1) return;
    if (Object.keys(p).length) updateItem(item.id, p);
  };

  const handled = useRef(false);
  const latest = useRef(save);
  latest.current = save;
  useEffect(
    () => () => {
      if (!handled.current && !readOnly) latest.current();
    },
    [readOnly]
  );
  const finish = (fn: () => void) => () => {
    handled.current = true;
    fn();
    onClose();
  };

  const total = numOrNull(price) !== null ? numOrNull(price)! * (numOrNull(qty) ?? 1) : null;

  return (
    <div className="tk-menu-panel tp-material">
      <div className="tk-menu-title">{isMaterial(item) ? 'Material' : 'Something to buy'}</div>
      {!isMaterial(item) && <p className="tk-menu-hint">Saving puts it on the project's shopping list.</p>}
      <div className="tk-segmented tk-segmented-sm tp-plan-kind" role="group" aria-label="Status">
        {MATERIAL_STATUS.map((s) => (
          <button key={s.label} type="button" className={status === s.progress ? 'is-active' : ''} onClick={() => setStatus(s.progress)} disabled={readOnly}>
            {s.label}
          </button>
        ))}
      </div>
      <div className="tp-material-row">
        <label className="tk-field">
          <span className="tk-field-label">How many</span>
          <input type="number" min={0} className="tk-input" value={qty} placeholder="1" onChange={(e) => setQty(e.target.value)} disabled={readOnly} />
        </label>
        <label className="tk-field">
          <span className="tk-field-label">Price each</span>
          <input type="number" min={0} step="0.01" className="tk-input" value={price} placeholder="$" onChange={(e) => setPrice(e.target.value)} disabled={readOnly} />
        </label>
      </div>
      {total !== null && <p className="tp-plan-info">Comes to {money(total)}</p>}
      <label className="tk-field">
        <span className="tk-field-label">From</span>
        <input className="tk-input" value={vendor} placeholder="Supplier or shop" onChange={(e) => setVendor(e.target.value)} disabled={readOnly} />
      </label>
      <label className="tk-field">
        <span className="tk-field-label">Link <span className="tk-muted">(optional)</span></span>
        <input className="tk-input" value={url} placeholder="https://" onChange={(e) => setUrl(e.target.value)} disabled={readOnly} />
      </label>
      {!readOnly && (
        <div className="tk-menu-actions">
          {isMaterial(item) && (
            <button type="button" className="tk-btn tk-btn-ghost" onClick={finish(() => updateItem(item.id, { node_kind: 'task' }))} title="Keep it, but not as something to buy">
              Not a material
            </button>
          )}
          <button type="button" className="tk-btn tk-btn-primary" onClick={finish(save)}>Save</button>
        </div>
      )}
    </div>
  );
};

export default MaterialPanel;
